/**
 * AgendamentoLocaisPorClienteSection — Epic LOCAIS-POR-CLIENTE Story 8.
 *
 * Substitui o `AgendamentoLocaisSection` flat. Renderiza accordions por
 * cliente com checkboxes pré-marcados a partir do endpoint preview agrupado.
 * Cada local físico compartilhado entre 2+ clientes recebe um badge
 * "compartilhado" em CADA accordion onde aparece.
 *
 * Source of truth: o hook `useContagemLocaisPorCliente` (passado via prop).
 * Este componente é puramente apresentacional.
 */
import React, { useMemo, useState } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Link2,
  MapPin,
  Phone,
  Plus,
  User as UserIcon,
  X,
} from 'lucide-react';

import type { Cliente } from '../../services/clienteService';
import type { LocalContagem } from '../../types/estabelecimento';
import type { ClienteLocaisPreview } from '../../types/contagemLocais';

interface AgendamentoLocaisPorClienteSectionProps {
  selectedHospitalId: number;
  selectedClienteIds: number[];
  clientesDisponiveis: Cliente[];
  // hook state
  previewData: ClienteLocaisPreview[];
  locaisPorCliente: Record<number, LocalContagem[]>;
  loadingPreview: boolean;
  errorPreview: string | null;
  // hook actions
  toggleLocal: (clienteId: number, local: LocalContagem) => void;
  adicionarLocalExtra: (clienteId: number, local: LocalContagem) => void;
  removerLocal: (clienteId: number, localId: number) => void;
  isLocalShared: (localId: number) => boolean;
  // UI control
  isReadOnly: boolean;
  totalLocaisSelecionados: number;
  // for "adicionar local extra" cache (flat hospital locais list)
  locaisContagemDisponiveis: LocalContagem[];
}

export const AgendamentoLocaisPorClienteSection: React.FC<
  AgendamentoLocaisPorClienteSectionProps
> = ({
  selectedHospitalId,
  selectedClienteIds,
  clientesDisponiveis,
  previewData,
  locaisPorCliente,
  loadingPreview,
  errorPreview,
  toggleLocal,
  adicionarLocalExtra,
  removerLocal,
  isLocalShared,
  isReadOnly,
  totalLocaisSelecionados,
  locaisContagemDisponiveis,
}) => {
  // Accordion expand state — auto-expand all clients by default on first load
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);

  // Auto-expand all clients that have at least one local on initial data load
  React.useEffect(() => {
    if (!hasInitializedExpansion && previewData.length > 0) {
      const allWithLocais = previewData
        .filter((c) => c.locais.length > 0)
        .map((c) => c.cliente_id);
      setExpandedClients(new Set(allWithLocais));
      setHasInitializedExpansion(true);
    }
  }, [previewData, hasInitializedExpansion]);

  // State for "+ Adicionar local extra" UI per cliente
  const [addingExtraForCliente, setAddingExtraForCliente] = useState<number | null>(null);
  const [extraLocalSelectedId, setExtraLocalSelectedId] = useState<number>(0);

  // Build a lookup for cliente fantasy names — fallback if preview cliente_nome is missing
  const clienteNameMap = useMemo(() => {
    const map = new Map<number, string>();
    clientesDisponiveis.forEach((c) => {
      map.set(c.id, c.nome_fantasia || c.razao_social);
    });
    return map;
  }, [clientesDisponiveis]);

  // Merge preview data with clientes that are selected but not in preview (empty locais)
  const displayGroups: ClienteLocaisPreview[] = useMemo(() => {
    const byId = new Map<number, ClienteLocaisPreview>();
    previewData.forEach((g) => byId.set(g.cliente_id, g));
    selectedClienteIds.forEach((cid) => {
      if (!byId.has(cid)) {
        byId.set(cid, {
          cliente_id: cid,
          cliente_nome: clienteNameMap.get(cid) || `Cliente ${cid}`,
          locais: [],
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      (a.cliente_nome || '').localeCompare(b.cliente_nome || ''),
    );
  }, [previewData, selectedClienteIds, clienteNameMap]);

  const toggleClienteAccordion = (clienteId: number) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clienteId)) next.delete(clienteId);
      else next.add(clienteId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedClients(new Set(displayGroups.map((g) => g.cliente_id)));
  };

  const collapseAll = () => {
    setExpandedClients(new Set());
  };

  const handleAdicionarExtra = (clienteId: number) => {
    if (!extraLocalSelectedId) return;
    const local = locaisContagemDisponiveis.find((l) => l.id === extraLocalSelectedId);
    if (!local) return;
    adicionarLocalExtra(clienteId, local);
    setAddingExtraForCliente(null);
    setExtraLocalSelectedId(0);
  };

  const cancelAdicionarExtra = () => {
    setAddingExtraForCliente(null);
    setExtraLocalSelectedId(0);
  };

  // ─── Early empty states ────────────────────────────────────────────────────
  const containerClass =
    'mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-4 border-2 border-emerald-200 dark:border-emerald-700';

  if (!selectedHospitalId) {
    return (
      <div className={containerClass}>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
          Locais Planejados por Cliente
        </label>
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          Selecione um hospital para ver os locais disponíveis por cliente
        </div>
      </div>
    );
  }

  if (selectedClienteIds.length === 0) {
    return (
      <div className={containerClass}>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
          Locais Planejados por Cliente
        </label>
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          Selecione ao menos um cliente para ver os locais vinculados
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
          Locais Planejados por Cliente
          {loadingPreview && (
            <span className="ml-2 text-xs text-blue-500 font-normal">Carregando...</span>
          )}
          {!loadingPreview && (
            <span className="ml-2 text-xs text-gray-500 font-normal">
              ({totalLocaisSelecionados} {totalLocaisSelecionados === 1 ? 'local' : 'locais'})
            </span>
          )}
        </label>
        {displayGroups.length > 0 && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="px-3 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              Expandir Todos
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors dark:bg-gray-700 dark:text-gray-300"
            >
              Recolher Todos
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {errorPreview && (
        <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 rounded-lg text-sm">
          {errorPreview}
        </div>
      )}

      {/* Accordions */}
      {!loadingPreview && displayGroups.length === 0 && !errorPreview && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          Nenhum dos clientes selecionados possui locais cadastrados para este hospital.
          Configure em Cadastros &gt; Clientes &gt; Vincular Locais.
        </div>
      )}

      <div className="space-y-2">
        {displayGroups.map((group) => {
          const isExpanded = expandedClients.has(group.cliente_id);
          const locaisSelecionados = locaisPorCliente[group.cliente_id] || [];
          const totalLinked = group.locais.length;
          const selecionadosIds = new Set(locaisSelecionados.map((l) => l.id));
          const linkedIds = new Set(group.locais.map((l) => l.id));
          const extrasSelecionados = locaisSelecionados.filter((l) => !linkedIds.has(l.id));

          return (
            <div
              key={group.cliente_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-emerald-200 dark:border-emerald-700 overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                type="button"
                onClick={() => toggleClienteAccordion(group.cliente_id)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 dark:hover:from-blue-900/40 dark:hover:to-indigo-900/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {group.cliente_nome}
                  </span>
                  <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded-full dark:bg-blue-900/40 dark:text-blue-200">
                    {locaisSelecionados.length} de {totalLinked}{' '}
                    {totalLinked === 1 ? 'local' : 'locais'}
                  </span>
                  {extrasSelecionados.length > 0 && (
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full dark:bg-amber-900/40 dark:text-amber-200">
                      +{extrasSelecionados.length} extra{extrasSelecionados.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {/* Accordion Body */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
                  {/* Warning when no linked locais */}
                  {totalLinked === 0 && extrasSelecionados.length === 0 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-sm text-amber-800 dark:text-amber-300">
                      <span>⚠️</span>
                      <div>
                        <div className="font-medium">Nenhum local vinculado para este cliente neste hospital.</div>
                        <div className="text-xs mt-1">
                          Adicione um local extra abaixo ou configure os vínculos em{' '}
                          <em>Cadastros &gt; Clientes &gt; Vincular Locais</em>.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Linked locais with checkboxes */}
                  {group.locais.map((local) => {
                    const isChecked = selecionadosIds.has(local.id);
                    const shared = isLocalShared(local.id);
                    return (
                      <label
                        key={`linked-${local.id}`}
                        className={`flex items-start gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${
                          isReadOnly ? 'cursor-not-allowed opacity-70' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isReadOnly}
                          onChange={() => {
                            toggleLocal(group.cliente_id, {
                              id: local.id,
                              local: local.nome,
                              responsavel: local.responsavel || '',
                              telefone: '',
                              ramal: '',
                              email: '',
                              ativo: true,
                            });
                          }}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-emerald-600 dark:text-emerald-400" />
                            <span className="font-medium text-sm text-gray-900 dark:text-white">
                              {local.nome}
                            </span>
                            {shared && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-medium rounded-full border border-blue-300 dark:border-blue-700"
                                title="Este local também é usado por outro cliente desta contagem"
                              >
                                <Link2 size={10} />
                                compartilhado
                              </span>
                            )}
                          </div>
                          {local.responsavel && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                              <UserIcon size={11} />
                              <span>{local.responsavel}</span>
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}

                  {/* Extras added manually */}
                  {extrasSelecionados.map((local) => {
                    const shared = isLocalShared(local.id);
                    return (
                      <div
                        key={`extra-${local.id}`}
                        className="flex items-center gap-3 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
                      >
                        <Plus size={14} className="text-amber-600 dark:text-amber-400" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900 dark:text-white">
                              {local.local}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 rounded">
                              extra
                            </span>
                            {shared && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-medium rounded-full border border-blue-300 dark:border-blue-700"
                                title="Este local também é usado por outro cliente"
                              >
                                <Link2 size={10} />
                                compartilhado
                              </span>
                            )}
                          </div>
                          {local.responsavel && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                              <UserIcon size={11} />
                              <span>{local.responsavel}</span>
                              {local.telefone && (
                                <>
                                  <span className="text-gray-300">|</span>
                                  <Phone size={11} />
                                  <span>{local.telefone}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => removerLocal(group.cliente_id, local.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Remover local extra"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* + Adicionar local extra */}
                  {!isReadOnly && (
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      {addingExtraForCliente === group.cliente_id ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={extraLocalSelectedId}
                            onChange={(e) => setExtraLocalSelectedId(Number(e.target.value))}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value={0}>Selecione um local extra...</option>
                            {locaisContagemDisponiveis
                              .filter(
                                (l) =>
                                  !linkedIds.has(l.id) &&
                                  !locaisSelecionados.some((sel) => sel.id === l.id),
                              )
                              .map((l) => (
                                <option key={l.id} value={l.id}>
                                  {l.local} {l.responsavel ? `(${l.responsavel})` : ''}
                                </option>
                              ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleAdicionarExtra(group.cliente_id)}
                            disabled={!extraLocalSelectedId}
                            className="px-3 py-2 text-sm bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Adicionar
                          </button>
                          <button
                            type="button"
                            onClick={cancelAdicionarExtra}
                            className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAddingExtraForCliente(group.cliente_id)}
                          className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                        >
                          <Plus size={14} />
                          Adicionar local extra
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AgendamentoLocaisPorClienteSection;
